import { useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogoLockup } from '@/components/brand/Logo';
import { GUIDES } from '@/modules/marketing/guides';

/**
 * The site navigation, shared by the landing page and every article.
 *
 * ## Why it is shared rather than copied
 *
 * Article pages used to carry a stripped header — a logo and one button — which made them read as
 * a different site that happened to use the same colours. A visitor arriving from a search result
 * had no way to reach pricing, or to see that monitoring existed at all. Same header everywhere is
 * the cheapest way to make the pages feel like one product.
 *
 * ## Section links have to work from two places
 *
 * `#pricing` means "a section of this page". Off the landing page those become real `/#pricing`
 * navigations. Only hash links are rewritten: an entry whose href is already a path was being
 * turned into `//features/...`, which a browser reads as protocol-relative and resolves to the host
 * `features`, so a menu entry led nowhere from every article and every guide.
 *
 * ## The mobile panel is an accordion, not a dump
 *
 * It used to render every group expanded, with each group's children in a two-column grid at
 * `text-xs`. On a phone that produced a wall five screens tall where long guide titles wrapped
 * across a column boundary — a menu you have to read rather than scan. Now each
 * group is a row you tap, one column, one open at a time, with tap targets big enough to hit.
 */

const NAV_LINKS: {
  href: string;
  label: string;
  items?: { label: string; to: string }[];
}[] = [
  {
    // Automation first, because it is what a customer does every day; the two products that keep a
    // fleet healthy afterwards follow it in the same order the landing page tells the story.
    href: '#automation',
    label: 'Automation',
    items: [
      { label: 'Playbooks', to: '/features/playbooks' },
      { label: 'Command runner', to: '/features/command-runner' },
      { label: 'Server templates', to: '/features/server-templates' },
      { label: 'Safe rollouts', to: '/features/safe-rollouts' },
      { label: 'Run history', to: '/features/run-history' },
      { label: 'Server management', to: '/features/server-management' },
      { label: 'Team access', to: '/features/team-management' },
    ],
  },
  {
    href: '#monitoring',
    label: 'Monitoring',
    items: [
      { label: 'Auto-Fix', to: '/features/auto-fix' },
      { label: 'Website monitoring', to: '/monitoring/website-monitoring' },
      { label: 'API monitoring', to: '/monitoring/api-monitoring' },
      { label: 'SSL certificate monitoring', to: '/monitoring/ssl-monitoring' },
      { label: 'DNS monitoring', to: '/monitoring/dns-monitoring' },
      { label: 'Domain expiry monitoring', to: '/monitoring/domain-expiry-monitoring' },
      { label: 'Port monitoring', to: '/monitoring/port-monitoring' },
    ],
  },
  {
    href: '/features/backups',
    label: 'Backups',
    items: [
      { label: 'Server backups', to: '/features/backups' },
      { label: 'Backup verification', to: '/features/backup-verification' },
      { label: 'All features', to: '/features' },
    ],
  },
  // Pricing sits ahead of Docs deliberately. Somebody who has read enough to want the price is
  // further down the decision than somebody looking for instructions, and documentation is the
  // one item people return to *after* signing up — so it ends the menu rather than interrupting
  // it.
  { href: '/pricing', label: 'Pricing' },
  {
    href: '/docs',
    label: 'Docs',
    // Derived from GUIDES rather than copied: a hand-written list of the titles went stale the
    // first time a guide was renamed, and the menu then advertised a page under a name it no
    // longer had. Capped, because a dropdown is a shortcut and /docs is the full list.
    items: [
      ...GUIDES.slice(0, 6).map((guide) => ({ label: guide.title, to: `/docs/${guide.slug}` })),
      { label: 'All documentation', to: '/docs' },
    ],
  },
];

export function SiteHeader({ onLanding = false }: { onLanding?: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const sectionHref = (href: string) => (href.startsWith('#') && !onLanding ? `/${href}` : href);

  const close = () => {
    setMenuOpen(false);
    setOpenGroup(null);
  };

  return (
    <header className="border-border/60 bg-background/80 sticky top-0 z-50 border-b backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3.5">
        <Link to="/" onClick={close} className="min-w-0">
          <LogoLockup size={38} subtitle />
        </Link>

        {/* Opened by hover *and* by keyboard focus, through `group-hover` and
            `group-focus-within` rather than state. A JS dropdown would need outside-click
            handling, an escape listener and focus management to reach the same place. */}
        <nav className="text-muted-foreground hidden items-center gap-1 text-sm lg:flex">
          {NAV_LINKS.map((link) =>
            link.items === undefined ? (
              <a
                key={link.href}
                href={sectionHref(link.href)}
                className="hover:text-foreground rounded-md px-3 py-2 transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <div key={link.href} className="group relative">
                <a
                  href={sectionHref(link.href)}
                  className="hover:text-foreground group-hover:text-foreground inline-flex items-center gap-1 rounded-md px-3 py-2 transition-colors"
                >
                  {link.label}
                  <ChevronDown
                    className="size-3.5 transition-transform duration-200 group-hover:rotate-180"
                    aria-hidden
                  />
                </a>
                <div className="invisible absolute top-full left-0 pt-2 opacity-0 transition-opacity duration-150 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                  <div className="border-border/60 bg-background w-64 rounded-xl border p-2 shadow-xl">
                    {link.items.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        className="text-muted-foreground hover:bg-muted/60 hover:text-foreground block rounded-lg px-3 py-2 text-sm transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ),
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/login"
            className="text-muted-foreground hover:text-foreground hidden rounded-md px-4 py-2 text-sm transition-colors sm:inline-flex"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap transition-opacity hover:opacity-90"
          >
            {/* Shorter on a phone, where the full label squeezed the logo's subtitle onto three lines. */}
            <span className="sm:hidden">Free trial</span>
            <span className="hidden sm:inline">Start free trial</span>
          </Link>
          {/* The CTA stays visible at every width — a menu that hides the only conversion path
              is a menu that costs signups. Only the navigation collapses. */}
          <button
            type="button"
            onClick={() => (menuOpen ? close() : setMenuOpen(true))}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="border-border/60 text-muted-foreground hover:text-foreground inline-flex size-9 items-center justify-center rounded-md border transition-colors lg:hidden"
          >
            {menuOpen ? <X className="size-4" aria-hidden /> : <Menu className="size-4" aria-hidden />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          // Capped and scrollable rather than however tall the content happens to be. The previous
          // panel ran past the bottom of the phone with no way back to the close button.
          className="border-border/60 bg-background max-h-[calc(100vh-4.5rem)] overflow-y-auto border-t lg:hidden"
        >
          <div className="mx-auto max-w-6xl px-4 py-2">
            {NAV_LINKS.map((link) => {
              if (link.items === undefined) {
                return (
                  <a
                    key={link.href}
                    href={sectionHref(link.href)}
                    onClick={close}
                    className="border-border/40 text-foreground hover:bg-muted/40 block border-b px-2 py-3.5 text-[15px] font-medium last:border-b-0"
                  >
                    {link.label}
                  </a>
                );
              }

              const open = openGroup === link.label;

              return (
                <div key={link.href} className="border-border/40 border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpenGroup(open ? null : link.label)}
                    aria-expanded={open}
                    className="text-foreground hover:bg-muted/40 flex w-full items-center justify-between px-2 py-3.5 text-left text-[15px] font-medium"
                  >
                    {link.label}
                    <ChevronDown
                      className={`text-muted-foreground size-4 transition-transform duration-200 ${
                        open ? 'rotate-180' : ''
                      }`}
                      aria-hidden
                    />
                  </button>

                  {open && (
                    <div className="pb-2">
                      {/* The section itself, first. Tapping "Features" to read about features and
                          only being offered ten sub-pages is a dead end. */}
                      <a
                        href={sectionHref(link.href)}
                        onClick={close}
                        className="text-primary hover:bg-muted/40 block rounded-md px-2 py-2.5 text-sm font-medium"
                      >
                        Overview
                      </a>
                      {link.items.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          onClick={close}
                          className="text-muted-foreground hover:bg-muted/40 hover:text-foreground block rounded-md px-2 py-2.5 text-sm"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <Link
              to="/login"
              onClick={close}
              className="text-muted-foreground hover:text-foreground border-border/40 block border-t px-2 py-3.5 text-[15px] sm:hidden"
            >
              Log in
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
